import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Send, Mail, MessageCircle, MessageSquare } from "lucide-react";
import { toast } from "sonner";

export default function AdminRetargetingPage() {
  const [vendors, setVendors] = useState([]);
  const [vendorId, setVendorId] = useState("");
  const [campaigns, setCampaigns] = useState([]);
  const [campaignId, setCampaignId] = useState("");
  const [channel, setChannel] = useState("email");
  const [body, setBody] = useState("Hi {name}, your coupon {coupon} is waiting at {store}!");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.adminVendors().then((r) => {
      const list = (r.data || []).filter((v) => v.status === "active");
      setVendors(list);
    });
  }, []);

  useEffect(() => {
    if (!vendorId) { setCampaigns([]); setCampaignId(""); return; }
    api.adminVendorCampaigns(vendorId).then((r) => {
      setCampaigns(r.data || []);
      if ((r.data || []).length) setCampaignId(r.data[0].id);
    });
  }, [vendorId]);

  const send = async () => {
    if (!vendorId || !campaignId) return toast.error("Pick a vendor and a campaign");
    if (!reason.trim()) return toast.error("Please add a reason for the manual send");
    setBusy(true);
    try {
      const r = await api.adminManualSend({ vendor_id: vendorId, campaign_id: campaignId, channel, body, reason });
      toast.success(`Sent to ${r.data.sent} lead(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-semibold">Manual retargeting</h1>
        <p className="text-muted-foreground text-sm">Send a message to a vendor's leads on their behalf (audit-logged).</p>
      </div>
      <Card className="p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Vendor</Label>
            <Select value={vendorId} onValueChange={setVendorId}>
              <SelectTrigger data-testid="admin-retarget-vendor"><SelectValue placeholder="Pick vendor" /></SelectTrigger>
              <SelectContent>
                {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.business}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Campaign</Label>
            <Select value={campaignId} onValueChange={setCampaignId} disabled={!campaigns.length}>
              <SelectTrigger data-testid="admin-retarget-campaign"><SelectValue placeholder="Pick campaign" /></SelectTrigger>
              <SelectContent>
                {campaigns.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <Tabs value={channel} onValueChange={setChannel}>
          <TabsList className="grid grid-cols-3 w-full md:w-fit">
            <TabsTrigger value="email"><Mail className="h-4 w-4 mr-1" /> Email</TabsTrigger>
            <TabsTrigger value="whatsapp"><MessageCircle className="h-4 w-4 mr-1" /> WhatsApp</TabsTrigger>
            <TabsTrigger value="sms"><MessageSquare className="h-4 w-4 mr-1" /> SMS</TabsTrigger>
          </TabsList>
          <TabsContent value={channel} className="mt-3 space-y-3">
            <Textarea rows={5} value={body} onChange={(e) => setBody(e.target.value)} data-testid="admin-retarget-body" />
            <Input placeholder="Reason for manual send (required, audited)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="admin-retarget-reason" />
            <Button onClick={send} disabled={busy} data-testid="admin-retarget-send">
              <Send className="h-4 w-4 mr-1" /> Send to all campaign leads
            </Button>
          </TabsContent>
        </Tabs>
      </Card>
    </div>
  );
}
